/* eslint-disable max-len */
import React, { useLayoutEffect, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Stack } from '@mui/material';
import PageShell from 'common/molecules/pageShell/PageShell';
import TitleAtom from 'common/atoms/title/TitleAtom';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import LoadingSpinner from '../../../../components/generic/LoadingSpinner';
import StepPageHeader from '../stepPageHeader/StepPageHeader';
import {
  contentStyles,
  childrenStyles,
  skipPersonalizationStyle,
} from './profileIntakePageStyles';
import appColors from '../../../../common/app_colors';
import appStrings from '../../../../common/app_strings';

const { text } = appColors;

const padding = 20;

const ProfileIntakePage = ({
  variant = 'step',
  paginationMeta = { current: 0, total: 0 },
  backgroundColor,
  backgroundOverlay = '',
  title = '',
  subtitle = '',
  isTitleTextWhite = false,
  NavComponent = null,
  children = null,
  hasLargeSubheader,
  titleStyleOverride = {},
  topBackArrowOnClickHandler,
  noAutoMarginOnChildren,
  splashPictureAlignsBottom,
  linkedTextUnderNavControl,
  linkedTextStyleOverride,
  linkedTextAction,
  exitIconOnClickOverride,
  childrenOverrideStyles,
  loadingText = '',
  isLoading = false,
  subtitle2 = '',
  hideChildren = false,
  editText = false,
  skipPersonalization,
}) => {
  const [keyboardIsOpen, setKeyboardIsOpen] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  if (isNative) {
    Keyboard.addListener('keyboardWillShow', () => {
      setKeyboardIsOpen(true);
    });
    Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardIsOpen(false);
    });
  }
  const isSplashPage = variant === 'splash';
  const isStepPage = variant === 'step';
  const isAnimationSplash = variant === 'animationSplash';
  const backgroundImage = isNative
    ? backgroundOverlay
      ? `url(${backgroundOverlay})`
      : ''
    : '';
  const titleStyles = isTitleTextWhite
    ? { ...titleStyleOverride, styles: { color: '#FFFFFF' } }
    : titleStyleOverride;
  const contentContainerStyles = isSplashPage ? {} : contentStyles;

  const conditionalChildrenStyles = isSplashPage && splashPictureAlignsBottom
    ? { margin: 'auto 0 0', paddingBottom: '0' }
    : isSplashPage || noAutoMarginOnChildren
      ? {}
      : { margin: 'auto 0' };

  const navContainerStyles = {
    height: 'fit-content',
    width: '100%',
    marginTop: splashPictureAlignsBottom ? '-4px' : '20px',
  };

  const initialPageContainerStyles = {
    height: splashPictureAlignsBottom ? '75vh' : `calc(100vh - ${padding * 2}px)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    padding: splashPictureAlignsBottom ? `12vh ${padding}px` : `${padding}px`,
  };

  const SkipPersonaliztion = (
    <Typography sx={skipPersonalizationStyle}>
      {appStrings.wholeMe.skipPersonalization}
    </Typography>
  );

  useEffect(() => () => {
    if (isNative) {
      Keyboard.removeAllListeners();
    }
  }, []);

  const [pageContainerStyles, setPageContainerStyles] = useState(initialPageContainerStyles);
  useLayoutEffect(() => {
    if (isStepPage) { // help responsiveness w/ keyboard present
      if (keyboardIsOpen) setPageContainerStyles({ ...initialPageContainerStyles, height: 'initial' });
      else setPageContainerStyles(initialPageContainerStyles);
    } else setPageContainerStyles(initialPageContainerStyles);
  }, [keyboardIsOpen, variant]);

  // Utility function to style specific words
  const styleText = (textMeta, highlightWords, highlightStyle) => {
    const escapedWords = highlightWords.map((word) => word.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')); // escape special chars
    const parts = textMeta.split(new RegExp(`(${escapedWords.join('|')})`, 'gi'));
    return parts.map((part) => (highlightWords.includes(part.toLowerCase()) ? (
      <span key={part} style={highlightStyle}>
        {part}
      </span>
    ) : (
      part
    )));
  };

  const renderContent = (
    <>
      <TitleAtom
        hasLargeSubheader={hasLargeSubheader}
        header={title}
        subHeader={!editText ? subtitle : styleText(subtitle, ['networks', 'shared affinity.', 'give back and get ahead.', 'thousands', "you'll fit right in!"], { color: text.highlightText })}
        subHeader2={!editText ? subtitle2 : styleText(subtitle2, ['career-path networks.'], { color: text.highlightText })}
        stylesOverride={titleStyles}
      />
      {!hideChildren && (
        Array.isArray(children) && children.length
          ? children.map((child) => (
            <div style={{ ...childrenStyles, ...conditionalChildrenStyles }}>
              {child}
            </div>
          ))
          : (
            <div style={{ ...childrenStyles, ...conditionalChildrenStyles, ...childrenOverrideStyles }}>
              {children}
            </div>
          )
      )}
      <div style={navContainerStyles}>{NavComponent}</div>
      {linkedTextUnderNavControl && <div style={{ ...linkedTextStyleOverride }} onClick={linkedTextAction}>{linkedTextUnderNavControl}</div>}
      {skipPersonalization && <div onClick={skipPersonalization}>{SkipPersonaliztion}</div>}
    </>
  );

  return (
    <PageShell styles={{ backgroundColor, backgroundImage }}>
      <div style={pageContainerStyles}>
        {isStepPage && (
          <StepPageHeader
            showProgressDots={!!paginationMeta.total}
            paginationMeta={paginationMeta}
            topBackArrowOnClickHandler={topBackArrowOnClickHandler}
            exitIconOnClickOverride={exitIconOnClickOverride}
          />
        )}
        {isSplashPage && renderContent}
        {isLoading
          && (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              sx={{
                paddingBottom: '10px',
              }}
            >
              {loadingText && (
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: 500,
                  marginRight: '10px',
                }}
              >
                {loadingText}
              </Typography>
              )}
              <LoadingSpinner
                sx={{ color: 'white' }}
                containerOverride={{ paddingBottom: '10px', mt: 0 }}
              />
            </Stack>
          )}
        {!isSplashPage && !isAnimationSplash && <Box style={contentContainerStyles}>{renderContent}</Box>}
      </div>
    </PageShell>
  );
};

export const ProfileIntakePagePropTypes = {
  variant: PropTypes.oneOf(['splash', 'step']),
  paginationMeta: PropTypes.shape({ current: PropTypes.number, total: PropTypes.number }),
  backgroundColor: PropTypes.string.isRequired,
  backgroundOverlay: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  subtitle2: PropTypes.string,
  isTitleTextWhite: PropTypes.bool,
  NavComponent: PropTypes.node,
  children: PropTypes.node,
  titleStyleOverride: PropTypes.oneOfType([PropTypes.object]),
  loadingText: PropTypes.string,
  isLoading: PropTypes.bool,
  hideChildren: PropTypes.bool,
  editText: PropTypes.bool,
};

export const ProfileIntakePageDefaultProps = {
  variant: 'step',
  paginationMeta: { current: 0, total: 0 },
  backgroundOverlay: '',
  title: '',
  subtitle: '',
  subtitle2: '',
  isTitleTextWhite: false,
  titleStyleOverride: {},
  loadingText: '',
  isLoading: false,
  children: null,
  NavComponent: null,
  hideChildren: false,
  editText: false,
};

ProfileIntakePage.propTypes = ProfileIntakePagePropTypes;

export default ProfileIntakePage;
